/**
 * Floating editor toolbar shown when editing a detected object layer.
 * Brush, eraser, move, zoom, undo/redo, slider, confirm/cancel.
 */
export default function EditorActionToolbar({
  activeTool,
  onToolChange,
  magicMode = 'add',
  onMagicModeChange,
  brushSize,
  onBrushSizeChange,
  onZoomIn,
  onZoomOut,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  className = '',
}) {
  const BRUSH_SIZE_MIN = 2;
  const BRUSH_SIZE_MAX = 50;
  const BRUSH_SIZE_MID = Math.round((BRUSH_SIZE_MIN + BRUSH_SIZE_MAX) / 2);
  const brushSizeValue = brushSize ?? BRUSH_SIZE_MID;
  const brushFillPct =
    BRUSH_SIZE_MAX > BRUSH_SIZE_MIN
      ? ((brushSizeValue - BRUSH_SIZE_MIN) / (BRUSH_SIZE_MAX - BRUSH_SIZE_MIN)) * 100
      : 0;

  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-[rgba(253, 253, 253, 0.1)] shadow-[0_5px_20px_rgba(0,0,0,0.18)] backdrop-blur-xl backdrop-saturate-150 border border-white ${className}`}
      onClick={(e) => e.stopPropagation()}
    >

    {/* Brush */}
    <button
        type="button"
        onClick={() => onToolChange?.('brush')}
        className={`p-2 rounded rounded-full hover:bg-primary-300 hover:text-white transition-colors ${activeTool === 'brush' ? 'bg-primary-300 text-white' : 'bg-white text-black hover:bg-gray-700'}`}
        title="Brush (B)"
      >
        <svg className="w-4 h-4" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
          <path d="M17.2973 4.66672C17.298 4.54951 17.2762 4.43333 17.2331 4.32482C17.1901 4.21631 17.1267 4.11762 17.0465 4.0344L13.3795 0.258287C13.2987 0.175745 13.2028 0.110443 13.0974 0.0661225C12.9921 0.0218025 12.8792 -0.000662761 12.7654 1.48856e-05C12.6516 -0.000662761 12.5388 0.0218025 12.4334 0.0661225C12.328 0.110443 12.2322 0.175745 12.1514 0.258287L9.7038 2.77867L0.250825 12.5128C0.170668 12.5961 0.107252 12.6948 0.0642123 12.8033C0.0211727 12.9118 -0.000643614 13.028 1.44556e-05 13.1452V16.9213C1.44556e-05 17.1575 0.0911339 17.384 0.253327 17.551C0.415521 17.718 0.635503 17.8119 0.864879 17.8119H4.53191C4.65293 17.8187 4.77398 17.7992 4.88721 17.7547C5.00044 17.7102 5.10333 17.6417 5.1892 17.5536L14.5903 7.81942L17.0465 5.34357C17.1253 5.25715 17.1896 5.15783 17.2368 5.04968C17.2451 4.97869 17.2451 4.90692 17.2368 4.83594C17.2408 4.79448 17.2408 4.75271 17.2368 4.71125L17.2973 4.66672ZM4.17731 16.0307H1.72974V13.5103L10.3179 4.66672L12.7654 7.1871L4.17731 16.0307ZM13.9849 5.93137L11.5373 3.41099L12.7654 2.15525L15.2043 4.66672L13.9849 5.93137Z" />
        </svg>
      </button>

      {/* Eraser */}
      <button
        type="button"
        onClick={() => onToolChange?.('eraser')}
        className={`p-2 rounded rounded-full hover:bg-primary-300 hover:text-white transition-colors ${activeTool === 'eraser' ? 'bg-primary-300 text-white' : 'bg-white text-black hover:bg-gray-700'}`}
        title="Eraser (E)"
      >
        <svg className="w-4 h-4" viewBox="0 0 17 17" fill="currentColor" aria-hidden="true">
          <path d="M12.1211 0.505946L16.4022 4.77838C17.0768 5.46162 17.0768 6.55135 16.4022 7.23459L8.45405 15.1827C7.80405 15.8302 6.92396 16.1937 6.00649 16.1937C5.08901 16.1937 4.20892 15.8302 3.55892 15.1827L0.505946 12.1297C-0.168649 11.4465 -0.168649 10.3568 0.505946 9.67351L9.67351 0.505946C10.3568 -0.168649 11.4465 -0.168649 12.1211 0.505946ZM1.72541 10.9016L4.78703 13.9546C5.46162 14.6378 6.55135 14.6378 7.23459 13.9546L10.2876 10.9016L6.00649 6.62054L1.72541 10.9016Z" />
        </svg>
      </button>

      {/* Magic wand */}
      <button
        type="button"
        onClick={() => onToolChange?.('magicwand')}
        className={`p-2 rounded rounded-full hover:bg-primary-300 hover:text-white transition-colors ${activeTool === 'magicwand' ? 'bg-primary-300 text-white' : 'bg-white text-black hover:bg-gray-700'}`}
        title="Magic wand (M)"
      >
        <svg className="w-4 h-4" viewBox="0 0 18 21" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" clipRule="evenodd" d="M6.29217 2.11606L17.6102 13.5629C17.7847 13.7428 17.8825 13.9847 17.8825 14.2367C17.8825 14.4887 17.7847 14.7306 17.6102 14.9105L14.9476 17.6036C14.771 17.7822 14.5316 17.8825 14.2819 17.8825C14.0322 17.8825 13.7928 17.7822 13.6163 17.6036L2.29825 6.15675C2.12331 5.97988 1.98454 5.76988 1.88986 5.53875C1.79518 5.30761 1.74645 5.05988 1.74645 4.80969C1.74645 4.55951 1.79518 4.31177 1.88986 4.08064C1.98454 3.84951 2.12331 3.63951 2.29825 3.46264L3.62956 2.11606C3.98268 1.75899 4.46155 1.5584 4.96086 1.5584C5.46018 1.5584 5.93905 1.75899 6.29217 2.11606ZM3.03075 9.04704C3.44878 9.04704 3.74442 9.30322 3.92519 9.64891L3.97509 9.75557C4.25065 10.4013 4.70312 10.9538 5.2791 11.3478L5.47682 11.4697C5.81388 11.6621 6.06056 11.9697 6.06056 12.3792C6.06056 12.7411 5.86943 13.0268 5.5898 13.2192L5.48152 13.2877C4.88009 13.6388 4.39265 14.1593 4.07866 14.7857L3.9798 14.9952C3.80374 15.3971 3.49774 15.7133 3.03075 15.7133C2.60518 15.7133 2.31425 15.4523 2.13254 15.1028L2.0817 14.9952C1.807 14.3513 1.35458 13.8011 0.778636 13.4106L0.584683 13.2906C0.246678 13.0982 0 12.7897 0 12.3792C0 12.0145 0.195836 11.7316 0.475467 11.5383L0.584683 11.4697C1.18362 11.1213 1.66866 10.6032 1.98001 9.97937L2.08641 9.75462C2.2587 9.3575 2.56941 9.04704 3.03075 9.04704ZM8.29008 6.83004L6.95877 8.17662L14.2819 15.5838L15.6132 14.2372L8.29008 6.83004ZM14.7348 5.14443C14.9589 4.68637 15.488 4.65589 15.7535 5.05206L15.8062 5.14348C16.0096 5.55774 16.3467 5.90915 16.7882 6.1339C17.2533 6.36817 17.2599 6.96241 16.792 7.19668C16.4217 7.38314 16.1106 7.67083 15.8938 8.02711L15.809 8.18329C15.5831 8.64612 15.0521 8.67659 14.7875 8.28043L14.7348 8.189C14.5513 7.81617 14.2665 7.50399 13.9138 7.28906L13.7537 7.19954C13.315 6.9786 13.2858 6.44721 13.6624 6.18722L13.749 6.1358C14.1216 5.9491 14.4341 5.65912 14.65 5.29966L14.7348 5.14443ZM4.96181 3.46168L3.62861 4.80922L5.62558 6.83004L6.95783 5.48251L4.96086 3.46264L4.96181 3.46168ZM11.0337 0C11.3105 0 11.5138 0.159037 11.6494 0.379976L11.7059 0.484731C11.93 0.938988 12.2878 1.33611 12.751 1.61514L12.9355 1.71703C13.1944 1.8475 13.3875 2.07225 13.3875 2.3808C13.3875 2.65031 13.2396 2.85696 13.0297 2.99029L12.9308 3.04742C12.4412 3.29503 12.0533 3.67024 11.7963 4.10926L11.7021 4.28354C11.5703 4.554 11.35 4.7616 11.0337 4.7616C10.7569 4.7616 10.5535 4.60256 10.4179 4.38163L10.3614 4.27687C10.1267 3.80702 9.76409 3.41482 9.31633 3.14647L9.13179 3.04457C8.87288 2.9141 8.67986 2.6884 8.67986 2.37985C8.67986 2.11129 8.82768 1.90464 9.03764 1.77036L9.1365 1.71513C9.62609 1.46562 10.014 1.09231 10.271 0.652339L10.3652 0.478065C10.497 0.207606 10.7173 0 11.0337 0Z" />
        </svg>
      </button>
      {activeTool === 'magicwand' && (
        <div className="flex items-center gap-1 rounded-full bg-gray-700 p-2  ml-1">
          <button
            type="button"
            onClick={() => onMagicModeChange?.('add')}
            className={`px-2 py-1 text-xs rounded-full transition-colors ${magicMode === 'add' ? 'bg-primary-200 text-white' : 'text-white hover:bg-primary-400'
              }`}
            title="Add region"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => onMagicModeChange?.('remove')}
            className={`px-2 py-1 text-xs rounded-full transition-colors ${magicMode === 'remove' ? 'bg-primary-200 text-white' : 'text-white hover:bg-primary-400'
              }`}
            title="Remove region"
          >
            Remove
          </button>
        </div>
      )}

    {/* Brush size — styled via .brush-size-slider in index.css */}
    <div
        className="group flex items-center gap-2 rounded-full bg-white h-[36px] px-3 py-1.5 text-primary-300 transition-colors hover:text-white hover:ring-white/20"
        title="Brush / eraser size"
      >
        <input
          type="range"
          min={BRUSH_SIZE_MIN}
          max={BRUSH_SIZE_MAX}
          value={brushSizeValue}
          onChange={(e) => onBrushSizeChange?.(Number(e.target.value))}
          className="brush-size-slider w-[7.25rem] min-w-[6.5rem] shrink-0"
          style={{ '--brush-fill': `${brushFillPct}%` }}
          aria-label="Brush size"
          title={`Brush size: ${brushSizeValue}`}
        />
      </div>
      
      {/* Zoom in */}
      <button type="button" onClick={onZoomIn} className="p-2 rounded rounded-full hover:bg-primary-300 hover:text-white bg-white text-black" title="Zoom in (scroll wheel)">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
          <path d="M11 8v6" />
          <path d="M8 11h6" />
        </svg>
      </button>
      {/* Zoom out */}
      <button type="button" onClick={onZoomOut} className="p-2 rounded rounded-full hover:bg-primary-300 hover:text-white bg-white text-black" title="Zoom out">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
          <path d="M8 11h6" />
        </svg>
      </button>

      {/* Move */}
      <button
        type="button"
        onClick={() => onToolChange?.('move')}
        className={`p-2 rounded rounded-full hover:bg-primary-300 hover:text-white ${activeTool === 'move' ? 'bg-primary-300 text-white' : 'bg-white text-black hover:bg-gray-700'}`}
        title="Move — hold Space to pan"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m5 9-3 3 3 3" />
          <path d="m9 5 3-3 3 3" />
          <path d="m15 19-3 3-3-3" />
          <path d="m19 9 3 3-3 3" />
          <path d="M2 12h20" />
          <path d="M12 2v20" />
        </svg>
      </button>
      
      {/* Undo */}
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        className={`p-2 rounded rounded-full bg-white ${canUndo ? 'text-black hover:bg-primary-300 hover:text-white' : 'text-gray-500 cursor-not-allowed opacity-50'}`}
        title="Undo (Z or Ctrl+Z)"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.5303 3.4697a.75.75 0 010 1.0606L5.8107 6.25H15a5 5 0 015 5 5 5 0 01-5 5H8a.75.75 0 010-1.5h7a3.5 3.5 0 003.5-3.5A3.5 3.5 0 0015 7.75H5.8107l1.7196 1.7197a.75.75 0 01-1.0606 1.0606l-3-3a.75.75 0 010-1.0606l3-3a.75.75 0 011.0606 0z"
          />
        </svg>
      </button>
      {/* Redo */}
      <button
        type="button"
        onClick={onRedo}
        disabled={!canRedo}
        className={`p-2 rounded rounded-full bg-white ${canRedo ? 'text-black hover:bg-primary-300 hover:text-white' : 'text-gray-500 cursor-not-allowed opacity-50'}`}
        title="Redo (Y or Ctrl+Y / Ctrl+Shift+Z)"
      >
      <svg
        className="w-4 h-4"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.4697 3.4697a.75.75 0 011.0606 0l3 3a.75.75 0 010 1.0606l-3 3a.75.75 0 11-1.0606-1.0606L18.1893 8.5H9A3.5 3.5 0 005.5 12 3.5 3.5 0 009 15.5h7a.75.75 0 010 1.5H9A5 5 0 014 12a5 5 0 015-5h9.1893l-1.7196-1.7197a.75.75 0 010-1.0606z"
        />
      </svg>
      </button>
      
    </div>
  );
}
